import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
	IChatData,
	IChatQuery,
	ICitationReference,
	IQuery,
	IQueryMessage,
	IReferenceItem,
	QueriesConfigType,
	QueryMessageType,
	ReferenceDrawerVisibilityType,
	StudyType,
	IFollowUps,
	IOptionGroup,
	IModeFilters,
	AskKuralModeType,
} from "../models/interfaces";
import { IFeature } from "src/pages/AdminCenter/models/interfaces";
import { IDeckModalState, IDeckPlanResponse } from "../models/pptInterfaces";
import { RootState } from "src/store";

interface askKuralState {
	askKuralDropdownData: { [key: string]: any[] };
	isAskKuralReferenceDrawerVisible: boolean;
	askKuralStudySelectDrawerVisible: boolean;
	savedQueries: IQuery[];
	selectedQuery: IQuery | null;
	newQuery: IQuery | null;
	loadingState: boolean;
	studyType: StudyType[];
	conversationId: string;
	selectedQueriesConfig: QueriesConfigType;
	isCompDisabled?: boolean;
	newChat?: IChatData | null;
	allChats: IChatData[];
	chatConfig: {
		page: number;
		pageSize: number;
		total: number | null;
	};
	hasMore: boolean;
	selectedChatData?: IChatData | null;
	selectedChatId?: string | null;
	selectedBranchId?: string | null;
	isChatAvailableForCurrentChat?: boolean | null;
	askKuralVersion?: { [key: string]: string };
	activeFeatures: IFeature[];
	isModalClosed?: boolean;
	abortStreamingAskKural: AbortController | null;
	abortStreamingRepaint: AbortController[];
	isAskKuralSidebarOpen: boolean;
	isLoading: boolean;
	error: boolean;
	chatError: boolean;
	isProcessComplete: boolean;
	referenceItems: {
		[key in StudyType]?: IReferenceItem[];
	};
	referenceDrawerVisibility: ReferenceDrawerVisibilityType;
	idConfigChat?: {
		branch_id?: string;
		chat_id?: string;
		message_id?: string[];
		query_id?: string[];
	};
	askKuralReferenceActiveTab: StudyType;
	selectedStudies: string[];
	selectedStudiesMap: { [key in StudyType]?: string[] };
	selectedData: string[];
	selectedDataMap: { [key in StudyType]?: string[] };
	isSavedQuery: boolean;
	selectedWidget: string | null;
	isStandardQueryMode: boolean;
	followUps: IFollowUps[];
	activeCitationReferences: {
		citationId: string;
		studyType: string;
		messageId: string;
		references: ICitationReference[];
	} | null;
	activeWidgetData: {
		widgetId: string;
		studyType: string;
		messageId: string;
		widgetData: any;
	};
	referenceSelectedItemTrigger: number;
	isButtonDisabled: boolean;
	isEnterDisabled: boolean;
	hasSeenReflectiveModeDot?: boolean;
	autoOpenTranscriptItem: ICitationReference | null;
	agenticWidgetMetaData: IOptionGroup[];
	isAgenticMetaDataPreSelected: boolean;
	nonReflectiveModeFilters: IModeFilters | null;
	reflectiveModeFilters: IModeFilters | null;
	reflectiveQueryCount: number;
	reflectiveModalDisplayedCount: number;
	isReflectiveFeedbackSubmitted: boolean;
	showReflectiveFeedbackModal: boolean;
	hasShownReflectiveFeedbackThisSession: boolean;
	selectedMode: AskKuralModeType;
	lastSelectedWidgetType: string | null;
	searchKeywordPills: Array<{ type: string; keyword: string }>;
	deckModalState: IDeckModalState;
}

export const initialState: askKuralState = {
	askKuralDropdownData: { transcripts: [], reports: [], notes: [], quant: [] },
	isAskKuralReferenceDrawerVisible: false,
	askKuralStudySelectDrawerVisible: false,
	savedQueries: [],
	selectedQuery: null,
	newQuery: null,
	loadingState: false,
	studyType: null,
	conversationId: null,
	selectedQueriesConfig: QueriesConfigType.AskKural,
	isCompDisabled: false,
	newChat: null,
	allChats: [],
	chatConfig: {
		page: 1,
		pageSize: 20,
		total: null,
	},
	hasMore: false,
	selectedChatData: null,
	selectedChatId: null,
	selectedBranchId: null,
	askKuralVersion: {
		cognitive: "v1.0",
		"ask-kural": "v2.0",
	},
	activeFeatures: [],
	isModalClosed: false,
	abortStreamingAskKural: null,
	abortStreamingRepaint: [] as AbortController[],
	isAskKuralSidebarOpen: false,
	isLoading: false,
	error: false,
	chatError: false,
	isProcessComplete: false,
	referenceItems: {},
	referenceDrawerVisibility: ReferenceDrawerVisibilityType.hidden,
	idConfigChat: {
		branch_id: null,
		chat_id: null,
		message_id: [],
		query_id: [],
	},
	askKuralReferenceActiveTab: null,
	selectedStudies: [],
	selectedStudiesMap: {},
	selectedData: [],
	selectedDataMap: {},
	isSavedQuery: false,
	selectedWidget: null,
	isStandardQueryMode: true,
	followUps: [],
	activeCitationReferences: null,
	activeWidgetData: null,
	referenceSelectedItemTrigger: 0,
	isButtonDisabled: false,
	isEnterDisabled: false,
	hasSeenReflectiveModeDot: false,
	autoOpenTranscriptItem: null,
	agenticWidgetMetaData: [],
	isAgenticMetaDataPreSelected: false,
	nonReflectiveModeFilters: null,
	reflectiveModeFilters: null,
	reflectiveQueryCount: 0,
	reflectiveModalDisplayedCount: 0,
	isReflectiveFeedbackSubmitted: false,
	showReflectiveFeedbackModal: false,
	hasShownReflectiveFeedbackThisSession: false,
	selectedMode: AskKuralModeType.Reflective,
	lastSelectedWidgetType: null,
	searchKeywordPills: [],
	deckModalState: {
		isOpen: false,
		deckData: null,
		messageId: null,
	},
};

const askKuralSlice = createSlice({
	name: "askKural",
	initialState,
	reducers: {
		setAskKuralDropdownData(state, action: PayloadAction<{ key: string; value: any[] }>) {
			const { key, value } = action.payload;
			state.askKuralDropdownData = {
				...state.askKuralDropdownData,
				[key]: value,
			};
		},
		resetAskKuralDropdownData(state) {
			state.askKuralDropdownData = { transcripts: [], reports: [], notes: [], quant: [] };
		},
		setIsAskKuralReferenceDrawerVisible(state, action: PayloadAction<boolean>) {
			state.isAskKuralReferenceDrawerVisible = action.payload;
		},
		setAskKuralStudySelectDrawerVisible(state, action: PayloadAction<boolean>) {
			state.askKuralStudySelectDrawerVisible = action.payload;
		},
		setAllQueries(state, action: PayloadAction<IQuery[]>) {
			state.savedQueries = action.payload;
		},
		addQuery(state, action: PayloadAction<IQuery>) {
			const existingQuery = state.savedQueries.find((query) => query.id === action.payload.id);
			if (!existingQuery) {
				state.savedQueries = [action.payload, ...state.savedQueries];
			}
		},
		deleteQuery(state, action: PayloadAction<string>) {
			state.savedQueries = state.savedQueries.filter((query) => query.id !== action.payload);
			if (state.selectedQuery && state.selectedQuery.id === action.payload) {
				state.selectedQuery = null;
			}
		},
		setSelectedQuery(state, action: PayloadAction<IQuery | null>) {
			state.selectedQuery = action.payload;
		},
		setNewQuery(state, action: PayloadAction<IQuery> | null) {
			state.newQuery = action.payload;
		},
		setLoadingState(state, action: PayloadAction<boolean>) {
			state.loadingState = action.payload;
		},
		setStudyType(state, action: PayloadAction<StudyType[]>) {
			state.studyType = action.payload;
		},
		setConversationId(state, action: PayloadAction<string>) {
			state.conversationId = action.payload;
		},
		setSelectedQueriesConfig(state, action: PayloadAction<QueriesConfigType>) {
			state.selectedQueriesConfig = action.payload;
		},
		setIsCompDisabled(state, action: PayloadAction<boolean>) {
			state.isCompDisabled = action.payload;
		},
		setAllChats(state, action: PayloadAction<IChatData[]>) {
			state.allChats = action.payload;
		},
		addChats(state, action: PayloadAction<IChatData[]>) {
			state.allChats = [...state.allChats, ...action.payload];
		},
		updateChatConfig(state, action: PayloadAction<{ page: number; pageSize: number; total: number | null }>) {
			state.chatConfig = action.payload;
		},
		setHasMore(state, action: PayloadAction<boolean>) {
			state.hasMore = action.payload;
		},
		setNewChat(state, action: PayloadAction<IChatData> | null) {
			state.newChat = action.payload;
		},
		setSelectedChatData(state, action: PayloadAction<Partial<IChatData> | null>) {
			if (action.payload === null) {
				state.selectedChatData = null;
			} else if (
				state.selectedChatData &&
				!action.payload.chat_id &&
				!action.payload.filters &&
				!action.payload.path &&
				!action.payload.queries
			) {
				state.selectedChatData = { ...state.selectedChatData, ...action.payload };
			} else {
				state.selectedChatData = action.payload as IChatData;
			}
		},
		deleteChat(state, action: PayloadAction<string>) {
			state.allChats = state.allChats.filter((chat) => chat?.chat_id !== action.payload);
			if (state.selectedChatData && state.selectedChatData?.chat_id === action.payload) {
				state.selectedChatData = null;
			}
		},
		addNewChat(state, action: PayloadAction<IChatData>) {
			const newChat = action.payload;

			const existingChat = state.allChats.find((chat) => chat?.chat_id === newChat?.chat_id);
			if (existingChat) {
				state.allChats = [newChat, ...state.allChats.filter((chat) => chat?.chat_id !== newChat?.chat_id)];
			}
			state.selectedChatData = newChat;
		},
		updateChatName(state, action: PayloadAction<{ chat_id: string; new_name: string; is_chat_available: boolean; branch_id?: string }>) {
			const { chat_id, new_name, is_chat_available, branch_id } = action.payload;
			state.allChats = state.allChats.map((chat) => {
				if (chat.chat_id === chat_id) {
					return {
						...chat,
						chat_name: new_name,
						is_chat_available,
						...(branch_id !== undefined && { branch_id }),
					};
				}
				return chat;
			});
			if (chat_id && chat_id === state.selectedChatData?.chat_id) {
				state.selectedChatData = { ...state.selectedChatData, chat_name: new_name };
			}
		},
		addToLeftPanel(state, action: PayloadAction<IChatData>) {
			const chat_detail = action.payload;
			state.allChats = [chat_detail, ...state.allChats.filter((chat) => chat?.chat_id !== chat_detail?.chat_id)];
			if (!state.isModalClosed) state.selectedChatData = chat_detail;
		},
		setAskKuralVersion(state, action: PayloadAction<{ configType: string; version: string }>) {
			const { configType, version } = action.payload;
			if (!state.askKuralVersion || typeof state.askKuralVersion !== "object") {
				state.askKuralVersion = {};
			}
			state.askKuralVersion[configType] = version;
		},
		setActiveFeatures: (state, action: PayloadAction<IFeature[]>) => {
			state.activeFeatures = action.payload;
		},
		setIsModalClosed(state, action: PayloadAction<boolean>) {
			state.isModalClosed = action.payload;
		},
		updateAbortStreamingAskKural(state, action: PayloadAction<AbortController | null>) {
			if (state.abortStreamingAskKural) {
				try {
					state.abortStreamingAskKural.abort("Aborted by user");
				} catch (error) {
					console.warn("Failed to abort", error);
				}
			}
			state.abortStreamingAskKural = action.payload;
		},
		addRepaintAbortController(state, action: PayloadAction<AbortController>) {
			state.abortStreamingRepaint.push(action.payload);
		},
		abortAllRepaintControllers(state) {
			state.abortStreamingRepaint.forEach((c) => c.abort("Aborted by user"));
			state.abortStreamingRepaint = [];
		},
		setReferenceDrawerVisibility(state, action: PayloadAction<ReferenceDrawerVisibilityType>) {
			state.referenceDrawerVisibility = action.payload;
		},
		setIsAskKuralSidebarOpen: (state, action: PayloadAction<boolean>) => {
			state.isAskKuralSidebarOpen = action.payload;
		},
		setIsLoading(state, action: PayloadAction<boolean>) {
			state.isLoading = action.payload;
		},

		setError(state, action: PayloadAction<boolean>) {
			state.error = action.payload;
		},
		setChatError(state, action: PayloadAction<boolean>) {
			state.chatError = action.payload;
		},
		setIsProcessComplete(state, action: PayloadAction<boolean>) {
			state.isProcessComplete = action.payload;
		},
		setSelectedChatId(state, action: PayloadAction<string>) {
			state.selectedChatId = action.payload;
			state.isProcessComplete = false;
		},
		setSelectedBranchId(state, action: PayloadAction<string>) {
			state.selectedBranchId = action.payload;
		},
		setIsChatAvailableForCurrentChat(state, action: PayloadAction<boolean | null>) {
			state.isChatAvailableForCurrentChat = action.payload;
		},
		setReferenceItems(state, action: PayloadAction<{ [key in StudyType]?: IReferenceItem[] }>) {
			state.referenceItems = {
				...state.referenceItems,
				...action.payload,
			};
		},
		setSelectedStudies(state, action: PayloadAction<string[]>) {
			state.selectedStudies = action.payload;
		},
		setSelectedStudiesMap(state, action: PayloadAction<{ [key in StudyType]?: string[] }>) {
			state.selectedStudiesMap = action.payload;
		},
		setSelectedData(state, action: PayloadAction<string[]>) {
			state.selectedData = action.payload;
		},
		setSelectedDataMap(state, action: PayloadAction<{ [key in StudyType]?: string[] }>) {
			state.selectedDataMap = action.payload;
		},
		resetReferenceItems(state) {
			if (state.studyType && Array.isArray(state.studyType)) {
				const resetItems: { [key in StudyType]?: IReferenceItem[] } = {};
				state.studyType.forEach((type) => {
					resetItems[type] = [];
				});
				state.referenceItems = resetItems;
			} else {
				state.referenceItems = {};
			}
		},
		updateIdConfigChat(state, action: PayloadAction<Partial<askKuralState["idConfigChat"]>>) {
			state.idConfigChat = {
				...state.idConfigChat,
				...action.payload,
			};
		},
		resetIdConfigChat(state) {
			state.idConfigChat = {
				branch_id: null,
				chat_id: null,
				message_id: [],
				query_id: [],
			};
		},
		setAskKuralReferenceActiveTab(state, action: PayloadAction<StudyType>) {
			state.askKuralReferenceActiveTab = action.payload;
		},
		setIsSavedQuery(state, action: PayloadAction<boolean>) {
			state.isSavedQuery = action.payload;
		},
		setIsStandardQueryMode(state, action: PayloadAction<boolean>) {
			state.isStandardQueryMode = action.payload;
		},
		setSelectedMode: (state, action: PayloadAction<AskKuralModeType>) => {
			state.selectedMode = action.payload;
		},
		setSelectedWidget: (state, action: PayloadAction<string | null>) => {
			state.selectedWidget = action.payload;
			if (action.payload) {
				state.lastSelectedWidgetType = action.payload;
			}
		},
		setLastSelectedWidgetType: (state, action: PayloadAction<string | null>) => {
			state.lastSelectedWidgetType = action.payload;
		},
		setFollowUps(state, action: PayloadAction<IFollowUps[]>) {
			state.followUps = action.payload;
		},
		// Atomic merge for streaming follow-ups. Multiple study-type stream
		// loops can fire ``follow_up`` events in interleaved order; doing the
		// read+merge+write in the reducer ensures the second arrival can't
		// overwrite the first by reading stale state via ``store.getState()``.
		// Payload shape mirrors the existing dict-keyed-by-study-type runtime
		// usage of ``state.followUps``.
		mergeFollowUps(state, action: PayloadAction<Record<string, string[]>>) {
			const incoming = action.payload || {};
			const current: Record<string, string[]> = ((state.followUps as unknown) as Record<string, string[]>) || {};
			const merged: Record<string, string[]> = { ...current };
			Object.keys(incoming).forEach((studyType) => {
				const arr = incoming[studyType];
				if (Array.isArray(arr)) {
					merged[studyType] = [...(merged[studyType] || []), ...arr];
				}
			});
			(state.followUps as unknown as Record<string, string[]>) = merged;
		},
		setActiveCitationReferences(
			state,
			action: PayloadAction<{
				citationId: string;
				studyType: string;
				messageId: string;
				references: ICitationReference[];
			} | null>,
		) {
			const { citationId, studyType, messageId, references } = action.payload;
			state.activeCitationReferences = { citationId, studyType, messageId, references };
		},
		resetActiveCitationReferences(state) {
			state.activeCitationReferences = null;
		},
		setActiveWidgetData: (
			state,
			action: PayloadAction<{
				widgetId: string;
				studyType: string;
				messageId: string;
				widgetData: any;
			}>,
		) => {
			const { widgetId, studyType, messageId, widgetData } = action.payload;
			state.activeWidgetData = { widgetId, studyType, messageId, widgetData };
		},
		resetActiveWidgetData: (state) => {
			state.activeWidgetData = null;
		},
		clearReferenceSelectedItemTrigger(state) {
			state.referenceSelectedItemTrigger = ((state.referenceSelectedItemTrigger ?? 0) + 1) % Number.MAX_SAFE_INTEGER || 1;
		},
		setIsButtonDisabled(state, action: PayloadAction<boolean>) {
			state.isButtonDisabled = action.payload;
		},
		setIsEnterDisabled(state, action: PayloadAction<boolean>) {
			state.isEnterDisabled = action.payload;
		},
		setHasSeenReflectiveModeDot(state, action: PayloadAction<boolean>) {
			state.hasSeenReflectiveModeDot = action.payload;
		},
		setAutoOpenTranscriptItem(state, action: PayloadAction<ICitationReference | null>) {
			state.autoOpenTranscriptItem = action.payload;
		},
		clearAutoOpenTranscriptItem(state) {
			state.autoOpenTranscriptItem = null;
		},
		setAgenticWidgetMetaData(state, action: PayloadAction<IOptionGroup[]>) {
			state.agenticWidgetMetaData = action.payload;
		},
		setAgenticMetaDataPreSelected(state, action: PayloadAction<boolean>) {
			state.isAgenticMetaDataPreSelected = action.payload;
		},
		// Save current mode's filter state
		saveCurrentModeFilters: (state, action: PayloadAction<IModeFilters>) => {
			if (state.isStandardQueryMode) {
				state.reflectiveModeFilters = action.payload;
			} else {
				state.nonReflectiveModeFilters = action.payload;
			}
		},
		clearModeFilters: (state) => {
			state.nonReflectiveModeFilters = null;
			state.reflectiveModeFilters = null;
		},
		setReflectiveFeedbackState: (
			state,
			action: PayloadAction<{
				queryCount: number;
				modalDisplayedCount: number;
				isFeedbackSubmitted: boolean;
			}>,
		) => {
			const { queryCount, modalDisplayedCount, isFeedbackSubmitted } = action.payload;
			state.reflectiveQueryCount = queryCount;
			state.reflectiveModalDisplayedCount = modalDisplayedCount;
			state.isReflectiveFeedbackSubmitted = isFeedbackSubmitted;
		},
		setShowReflectiveFeedbackModal: (state, action: PayloadAction<boolean>) => {
			state.showReflectiveFeedbackModal = action.payload;
		},
		incrementReflectiveQueryCount: (state) => {
			state.reflectiveQueryCount += 1;
		},
		markReflectiveFeedbackModalShown: (state) => {
			state.reflectiveModalDisplayedCount += 1;
			state.hasShownReflectiveFeedbackThisSession = true;
		},
		markReflectiveFeedbackSubmitted: (state) => {
			state.isReflectiveFeedbackSubmitted = true;
			state.reflectiveModalDisplayedCount += 1;
			state.hasShownReflectiveFeedbackThisSession = true;
		},
		setHasShownReflectiveFeedbackThisSession: (state, action: PayloadAction<boolean>) => {
			state.hasShownReflectiveFeedbackThisSession = action.payload;
		},
		setSearchKeywordPills(state, action: PayloadAction<Array<{ type: string; keyword: string }>>) {
			state.searchKeywordPills = action.payload;
		},
		openDeckModal(state, action: PayloadAction<{ deckData: IDeckPlanResponse; messageId: string }>) {
			state.deckModalState = {
				isOpen: true,
				deckData: action.payload.deckData,
				messageId: action.payload.messageId,
			};
		},
		closeDeckModal(state) {
			state.deckModalState = {
				isOpen: false,
				deckData: null,
				messageId: null,
			};
		},
		setDeckData(state, action: PayloadAction<IDeckPlanResponse>) {
			state.deckModalState.deckData = action.payload;
		},
	},
});

export const {
	setAskKuralDropdownData,
	setIsAskKuralReferenceDrawerVisible,
	setAskKuralStudySelectDrawerVisible,
	setAllQueries,
	addQuery,
	deleteQuery,
	setSelectedQuery,
	setNewQuery,
	setLoadingState,
	setStudyType,
	setConversationId,
	setSelectedQueriesConfig,
	setIsCompDisabled,
	setAllChats,
	updateChatConfig,
	setHasMore,
	addChats,
	setNewChat,
	addNewChat,
	setSelectedChatData,
	setReferenceDrawerVisibility,
	deleteChat,
	updateChatName,
	setSelectedChatId,
	setSelectedBranchId,
	setIsChatAvailableForCurrentChat,
	addToLeftPanel,
	setAskKuralVersion,
	setActiveFeatures,
	setIsModalClosed,
	updateAbortStreamingAskKural,
	setIsAskKuralSidebarOpen,
	setIsLoading,
	setError,
	setChatError,
	setIsProcessComplete,
	setReferenceItems,
	resetReferenceItems,
	updateIdConfigChat,
	resetIdConfigChat,
	setAskKuralReferenceActiveTab,
	setSelectedStudies,
	setSelectedStudiesMap,
	setSelectedData,
	setSelectedDataMap,
	resetAskKuralDropdownData,
	setIsSavedQuery,
	setSelectedWidget,
	setIsStandardQueryMode,
	setSelectedMode,
	setLastSelectedWidgetType,
	setFollowUps,
	mergeFollowUps,
	setActiveCitationReferences,
	resetActiveCitationReferences,
	setActiveWidgetData,
	resetActiveWidgetData,
	clearReferenceSelectedItemTrigger,
	setIsButtonDisabled,
	setIsEnterDisabled,
	setHasSeenReflectiveModeDot,
	setAutoOpenTranscriptItem,
	clearAutoOpenTranscriptItem,
	setAgenticWidgetMetaData,
	setAgenticMetaDataPreSelected,
	saveCurrentModeFilters,
	clearModeFilters,
	setShowReflectiveFeedbackModal,
	setReflectiveFeedbackState,
	incrementReflectiveQueryCount,
	markReflectiveFeedbackModalShown,
	markReflectiveFeedbackSubmitted,
	setHasShownReflectiveFeedbackThisSession,
	addRepaintAbortController,
	abortAllRepaintControllers,
	setSearchKeywordPills,
	openDeckModal,
	closeDeckModal,
	setDeckData,
} = askKuralSlice.actions;

export default askKuralSlice.reducer;
export const selectIsSavedQuery = (state: RootState) => state.askKuralSlice.isSavedQuery;
